import { makeAutoObservable, runInAction } from "mobx";
import {
  dadataControllerSuggestParty,
  dadataControllerSuggestBank,
  dadataControllerSuggestAddress,
} from "@pro-sto/api-client";
import type { DadataSuggestion, LegalFormData } from "../legal-types";

// ─── DaData-стор: подсказки для орг., банков и адресов ───────────────────────

export class DadataStore {
  orgSuggestions: DadataSuggestion[] = [];
  bankSuggestions: DadataSuggestion[] = [];
  addrSuggestions: DadataSuggestion[] = [];
  showOrgSuggestions = false;
  showBankSuggestions = false;
  showAddrSuggestions = false;

  private _orgTimer: ReturnType<typeof setTimeout> | null = null;
  private _bankTimer: ReturnType<typeof setTimeout> | null = null;
  private _addrTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    makeAutoObservable<DadataStore, "_orgTimer" | "_bankTimer" | "_addrTimer">(
      this,
      { _orgTimer: false, _bankTimer: false, _addrTimer: false },
    );
  }

  // ── Visibility toggles ──────────────────────────────────────────────────────

  setShowOrgSuggestions = (visible: boolean) => {
    this.showOrgSuggestions = visible;
  };

  setShowBankSuggestions = (visible: boolean) => {
    this.showBankSuggestions = visible;
  };

  setShowAddrSuggestions = (visible: boolean) => {
    this.showAddrSuggestions = visible;
  };

  // ── Debounced API calls ─────────────────────────────────────────────────────

  suggestOrg = (query: string) => {
    if (this._orgTimer) clearTimeout(this._orgTimer);
    if (query.length < 2) {
      this.orgSuggestions = [];
      return;
    }
    this._orgTimer = setTimeout(async () => {
      try {
        const result = await dadataControllerSuggestParty({
          requestBody: { query },
        });
        const suggestions = this._parseSuggestions(result);
        runInAction(() => {
          this.orgSuggestions = suggestions;
        });
      } catch {
        /* ignore */
      }
    }, 800);
  };

  suggestBank = (query: string) => {
    if (this._bankTimer) clearTimeout(this._bankTimer);
    if (query.length < 2) {
      this.bankSuggestions = [];
      return;
    }
    this._bankTimer = setTimeout(async () => {
      try {
        const result = await dadataControllerSuggestBank({
          requestBody: { query },
        });
        const suggestions = this._parseSuggestions(result);
        runInAction(() => {
          this.bankSuggestions = suggestions;
        });
      } catch {
        /* ignore */
      }
    }, 800);
  };

  suggestAddress = (query: string) => {
    if (this._addrTimer) clearTimeout(this._addrTimer);
    if (query.length < 2) {
      this.addrSuggestions = [];
      return;
    }
    this._addrTimer = setTimeout(async () => {
      try {
        const result = await dadataControllerSuggestAddress({
          requestBody: { query },
        });
        const suggestions = this._parseSuggestions(result);
        runInAction(() => {
          this.addrSuggestions = suggestions;
        });
      } catch {
        /* ignore */
      }
    }, 800);
  };

  // ── Field extractors (вызываются координатором при выборе подсказки) ─────────

  extractOrgFields = (s: DadataSuggestion): Partial<LegalFormData> => {
    const d = s.data as Record<string, unknown>;
    const name = (d.name as Record<string, string> | undefined) ?? {};
    const opf = (d.opf as Record<string, string> | undefined) ?? {};
    const management =
      (d.management as Record<string, string> | undefined) ?? {};
    const directorParts = (management.name ?? "").trim().split(/\s+/);
    return {
      orgSearch: name.short_with_opf ?? s.value,
      legalName: name.short_with_opf ?? "",
      legalFullName: name.full_with_opf ?? "",
      inn: String(d.inn ?? ""),
      ogrn: String(d.ogrn ?? ""),
      kpp: String(d.kpp ?? ""),
      orgForm: opf.short ?? "",
      directorLastName: directorParts[0] ?? "",
      directorFirstName: directorParts[1] ?? "",
      directorMiddleName: directorParts.slice(2).join(" "),
    };
  };

  extractBankFields = (s: DadataSuggestion): Partial<LegalFormData> => {
    const d = s.data as Record<string, unknown>;
    const name = (d.name as Record<string, string> | undefined) ?? {};
    return {
      bankSearch: s.value,
      bankName: name.payment ?? name.short ?? s.value,
      bik: String(d.bic ?? ""),
      corrAccount: String(d.correspondent_account ?? ""),
    };
  };

  extractAddrFields = (s: DadataSuggestion): Partial<LegalFormData> => {
    const d = s.data as Record<string, unknown>;
    return {
      addressSearch: s.value,
      city: String(d.city ?? ""),
      street: String(d.street_with_type ?? d.street ?? ""),
      building: String(d.house ?? ""),
      office: String(d.flat ?? ""),
      postalCode: String(d.postal_code ?? ""),
    };
  };

  // ── Reset ───────────────────────────────────────────────────────────────────

  reset = () => {
    if (this._orgTimer) {
      clearTimeout(this._orgTimer);
      this._orgTimer = null;
    }
    if (this._bankTimer) {
      clearTimeout(this._bankTimer);
      this._bankTimer = null;
    }
    if (this._addrTimer) {
      clearTimeout(this._addrTimer);
      this._addrTimer = null;
    }
    this.orgSuggestions = [];
    this.bankSuggestions = [];
    this.addrSuggestions = [];
    this.showOrgSuggestions = false;
    this.showBankSuggestions = false;
    this.showAddrSuggestions = false;
  };

  // ── Private ─────────────────────────────────────────────────────────────────

  private _parseSuggestions = (raw: unknown): DadataSuggestion[] => {
    if (!raw || typeof raw !== "object") return [];
    const arr = (raw as Record<string, unknown>)["suggestions"];
    if (!Array.isArray(arr)) return [];
    return arr
      .filter(
        (item): item is Record<string, unknown> =>
          !!item && typeof item === "object",
      )
      .map((item) => ({
        value: String(item["value"] ?? ""),
        data: (item["data"] as Record<string, unknown>) ?? {},
      }));
  };
}
